import React from "react";
import * as R from "ramda";
import { Modal } from "antd";

import i18n from "../i18n";
import { CANDIDATE_TYPES } from "../constants/opportunity";

import { idInList } from "./candidates";
import { listHasItems } from "./arrays";
import api from "./api";

const errorMessage = (type, suffix, plural, absolute) =>
  `error${type}${absolute ? (plural ? "All" : "Single") : ""}Candidate${
    plural ? "s" : ""
  }${suffix}`;

export const retryBatchModal = ({
  action,
  actions,
  allFailed,
  candidates,
  retryCallback,
  failedIds,
  place,
  plural,
  single,
  translationOptions = {}
}) => {
  const type = "Moving";
  Modal.confirm({
    icon: "exclamation-circle",
    title: i18n.t(
      `opportunity:candidates.multipleSelection.${errorMessage(
        type,
        "Title",
        plural,
        allFailed || single
      )}`
    ),
    content: (
      <>
        <p data-test="retry-modal-content-1">
          {i18n.t(
            `opportunity:candidates.multipleSelection.${errorMessage(
              type,
              "Content1",
              plural,
              allFailed || single
            )}`,
            {
              failed: R.length(failedIds),
              count: R.length(candidates),
              action: actions[action](translationOptions)
            }
          )}
        </p>
        <ul data-test="retry-modal-candidates-list">
          {R.compose(
            R.map(({ id, fullname, first, last }) => (
              <li key={id}>{`${fullname}` || `${first} ${last}`}</li>
            )),
            R.filter(idInList(failedIds))
          )(candidates)}
        </ul>
        {/* TODO fix this message so it can show more stuff */}
        <p data-test="retry-modal-content-2">
          {i18n.t(
            `opportunity:candidates.multipleSelection.${errorMessage(
              type,
              "Content2",
              plural,
              false
            )}`,
            { place }
          )}
        </p>
      </>
    ),
    onOk: () =>
      retryCallback({
        action,
        candidates: R.filter(idInList(failedIds), candidates)
      }),
    cancelText: i18n.t("global:return"),
    okText: i18n.t("global:tryAgain"),
    okButtonProps: { "data-test": "retry-modal-confirm" }
  });
};

export const retryBatchInvite = ({
  action,
  actions,
  allFailed,
  candidates,
  retryCallback,
  failedIds,
  plural,
  single,
  translationOptions = {}
}) => {
  const type = "Inviting";
  Modal.confirm({
    icon: "exclamation-circle",
    title: i18n.t(
      `opportunity:candidates.multipleSelection.${errorMessage(
        type,
        "Title",
        plural,
        allFailed || single
      )}`
    ),
    content: (
      <>
        <p data-test="retry-modal-content-1">
          {i18n.t(
            `opportunity:candidates.multipleSelection.${errorMessage(
              type,
              "Content1",
              plural,
              allFailed || single
            )}`,
            {
              failed: R.length(failedIds),
              count: R.length(candidates),
              action: actions[action](translationOptions)
            }
          )}
        </p>
        <ul data-test="retry-modal-candidates-list">
          {R.compose(
            R.map(({ id, fullname, first, last }) => (
              <li key={id}>{`${fullname}` || `${first} ${last}`}</li>
            )),
            R.filter(idInList(failedIds))
          )(candidates)}
        </ul>
        <p data-test="retry-modal-content-2">
          {i18n.t(
            `opportunity:candidates.multipleSelection.${errorMessage(
              type,
              "Content2",
              plural,
              false
            )}`
          )}
        </p>
      </>
    ),
    onOk: () =>
      retryCallback({
        action,
        candidates: R.filter(idInList(failedIds), candidates)
      }),
    cancelText: i18n.t("global:return"),
    okText: i18n.t("global:tryAgain"),
    okButtonProps: { "data-test": "retry-modal-confirm" }
  });
};

export const retryBatchShare = ({ retryCallback, plural }) => {
  const type = "Inviting";
  Modal.confirm({
    icon: "exclamation-circle",
    title: i18n.t(
      `components:ShareCandidatesModal.${
        plural ? "inviteToOpportunitiesFailed" : "inviteToOpportunityFailed"
      }`
    ),
    content: (
      <p data-test="retry-modal-content-2">
        {i18n.t(
          `opportunity:candidates.multipleSelection.${errorMessage(
            type,
            "Content2",
            plural,
            false
          )}`
        )}
      </p>
    ),
    onOk: retryCallback,
    cancelText: i18n.t("global:return"),
    okText: i18n.t("global:tryAgain"),
    okButtonProps: { "data-test": "retry-modal-confirm" }
  });
};

export const makeBatchApiRequest = ({
  action,
  actions,
  candidates,
  emptyBody = false,
  idCompany,
  idOpportunity,
  type,
  ...rest
}) =>
  listHasItems(candidates) && actions[action][type]
    ? api.batchAction({
        action: actions[action][type],
        candidates: R.map(R.prop("id"), candidates),
        emptyBody,
        idCompany,
        idOpportunity,
        type,
        ...rest
      })
    : Promise.resolve(null);

/* For this request to fail, the structure of the response has to be the following:
  Either an external response:
  {
    stats<Stats>: {
      success<Number>: >= 0,
      fails<Number>: > 0
    },
    response<ExternalResponseArray>: [
      <ExternalResponse>: {
        requestIdentifier<ExternalRequestIdentifier>: {
          pathParameters<ExternalPathParameters>:
            {
              idOpportunity<String>,
              idCandidate<String>
            },
            requestBody<ExternalRequestBody>: {
              enabled<Boolean>
            }
          },
        result<ErrorJSON>: "{ "message<String>" }"
      }
    ]
  }
  Or an internal response:
  {
    stats<Stats>: {
      success<Number>: >= 0,
      fails<Number>: > 0
    },
    response<InternalResponseArray>: [
      <InternalResponse>: {
        requestIdentifier<InternalRequestIdentifier>: {
          pathParameters<InternalPathParameters>:
            {
              idCompany<String>
              idOpportunity<String>,
            },
            requestBody<InternalRequestBody>: {
              idCandidate<String>
            }
          },
        result<ErrorJSON>: "{ "message<String>" }"
      }
    ]
  }
*/
const getFails = external =>
  R.ifElse(
    // Check if the request has fails, returns false if there's no request
    R.compose(
      Boolean,
      R.path(["stats", "fails"])
    ),
    R.pipe(
      R.prop("response"),
      R.filter(
        R.when(
          R.prop("result"),
          R.pipe(
            R.propOr({}, "result"),
            JSON.parse,
            R.prop("message")
          )
        )
      ),
      R.map(
        R.path([
          "requestIdentifier",
          external ? "pathParameters" : "requestBody",
          "idCandidate"
        ])
      )
    ),
    R.always([])
  );

export const getBatchExternalFails = getFails(true);
export const getBatchInternalFails = getFails(false);

export function getCandidateTypes(candidates) {
  const [internalCandidates, externalCandidates] = R.partition(
    R.propOr(true, "isRegistered"),
    candidates
  );

  const [completedScreeningCandidates, statusScreeningCandidates] = R.partition(
    R.propSatisfies(
      R.includes(R.__, CANDIDATE_TYPES.internal.completedScreening.status),
      "screeningStatus"
    ),
    internalCandidates
  );

  return {
    completedScreeningCandidates,
    externalCandidates,
    internalCandidates,
    statusScreeningCandidates
  };
}

export function moveCandidatesToProcessConfirm({
  onOk,
  plurals,
  pluralsPending,
  pluralsRejected,
  rejectedCount,
  selectedCount,
  pendingCount
}) {
  Modal.confirm({
    title: i18n.t(
      `opportunity:candidates.multipleSelection.${
        plurals
          ? "confirmMovePendingCandidatesToProcessTitle"
          : "confirmMovePendingCandidateToProcessTitle"
      }`,
      {
        count: selectedCount
      }
    ),
    content: (
      <>
        <ul>
          {rejectedCount > 0 && (
            <li>
              {i18n.t(
                `opportunity:candidates.multipleSelection.${
                  pluralsRejected
                    ? "confirmMoveRejectedCandidatesToProcessContent1"
                    : plurals
                    ? "confirmMoveRejectedCandidateToProcessContent1"
                    : "confirmMoveRejectedSingleCandidateToProcessContent1"
                }`,
                {
                  count: rejectedCount
                }
              )}
            </li>
          )}
          {pendingCount > 0 && (
            <li>
              {i18n.t(
                `opportunity:candidates.multipleSelection.${
                  pluralsPending
                    ? "confirmMovePendingCandidatesToProcessContent1"
                    : plurals
                    ? "confirmMovePendingCandidateToProcessContent1"
                    : "confirmMovePendingSingleCandidateToProcessContent1"
                }`,
                { count: pendingCount }
              )}
            </li>
          )}
        </ul>
        {pendingCount > 0 && (
          <p>
            {i18n.t(
              `opportunity:candidates.multipleSelection.${
                pluralsPending
                  ? "confirmMovePendingCandidatesToProcessContent2"
                  : "confirmMovePendingCandidateToProcessContent2"
              }`
            )}
          </p>
        )}
      </>
    ),
    autoFocusButton: "cancel",
    okButtonProps: {
      "data-test": "multiple-candidates-move-to-process-confirm"
    },
    cancelButtonProps: {
      "data-test": "multiple-candidates-move-to-process-cancel"
    },
    cancelText: i18n.t("global:cancel"),
    okText: i18n.t("global:continue"),
    onOk
  });
}

export function moveCandidatesToRejectedConfirm({ count, plurals, onOk }) {
  Modal.confirm({
    title: i18n.t(
      `opportunity:candidates.multipleSelection.${
        plurals ? "confirmRejectCandidates" : "confirmRejectCandidate"
      }`,
      { count }
    ),
    autoFocusButton: "cancel",
    cancelText: i18n.t("global:cancel"),
    okText: i18n.t("global:continue"),
    okButtonProps: {
      "data-test": "multiple-candidates-move-to-rejected-confirm"
    },
    cancelButtonProps: {
      "data-test": "multiple-candidates-move-to-rejected-cancel"
    },
    onOk
  });
}
